import React, { useState, useEffect } from 'react';
import { Download, FileText, CheckCircle, Send, Loader2, Trash2 } from 'lucide-react';
import Notification from '../../Component/Notification';

interface Invoice {
    id: number;
    clientName: string;
    clientEmail: string;
    projectTitle: string;
    totalAmount: number;
    discountAmount: number;
    hasAdvance: boolean;
    advanceAmount: number;
    remainingAmount: number;
    paymentDate: string;
    generatedAt: string;
    pdfPath: string;
}

const InvoiceHistory: React.FC = () => {
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [sendingId, setSendingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [notify, setNotify] = useState({ isOpen: false, message: '', type: 'success' as 'success' | 'error' });

    useEffect(() => {
        fetchInvoices();
    }, []);

    const fetchInvoices = () => {
        fetch('https://www.projectinfotech.in/api/invoice/history')
        // fetch('http://localhost:2025/api/invoice/history')
            .then(res => res.json())
            .then(data => { setInvoices(data); setLoading(false); })
            .catch(err => { console.error(err); setLoading(false); });
    };

    const handleDownload = (id: number) => {
        window.open(`https://www.projectinfotech.in/api/invoice/download/${id}`, '_blank');
        // window.open(`http://localhost:2025/api/invoice/download/${id}`, '_blank');
    };

    const handleSendEmail = async (id: number) => {
        setSendingId(id);
        try {
            const res = await fetch(`https://www.projectinfotech.in/api/invoice/send/${id}`, { method: 'POST' });
            // const res = await fetch(`http://localhost:2025/api/invoice/send/${id}`, { method: 'POST' });
            if (res.ok) {
                setNotify({ isOpen: true, message: 'Invoice emailed to client successfully!', type: 'success' });
            } else {
                setNotify({ isOpen: true, message: 'Failed to send email.', type: 'error' });
            }
        } catch (error) {
            setNotify({ isOpen: true, message: 'Server error.', type: 'error' });
        } finally {
            setSendingId(null);
        }
    };

    // --- NEW: DELETE HANDLER ---
    const handleDelete = async (id: number) => {
        if (!window.confirm("Are you sure you want to delete this invoice? This action cannot be undone.")) return;
        
        setDeletingId(id);
        try {
            const res = await fetch(`https://www.projectinfotech.in/api/invoice/delete/${id}`, { method: 'DELETE' });
            // const res = await fetch(`http://localhost:2025/api/invoice/delete/${id}`, { method: 'DELETE' });
            if (res.ok) {
                setNotify({ isOpen: true, message: 'Invoice deleted successfully!', type: 'success' });
                // Remove from state instantly without refreshing the page
                setInvoices(invoices.filter(inv => inv.id !== id));
            } else {
                setNotify({ isOpen: true, message: 'Failed to delete invoice.', type: 'error' });
            }
        } catch (error) {
            setNotify({ isOpen: true, message: 'Server error.', type: 'error' });
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen animate-fade-in-up">
            <Notification isOpen={notify.isOpen} message={notify.message} type={notify.type} onClose={() => setNotify({ ...notify, isOpen: false })} />

            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <FileText className="text-red-600" size={28}/> Invoice History
            </h1>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden w-full">
                {/* --- RESPONSIVE WRAPPER (overflow-x-auto makes it scrollable on mobile) --- */}
                <div className="overflow-x-auto w-full">
                    <table className="w-full text-left min-w-[800px]">
                        <thead className="bg-gray-100/80 border-b border-gray-200">
                            <tr>
                                <th className="p-4 md:p-5 font-bold text-gray-600 uppercase text-xs tracking-wider">Client Details</th>
                                <th className="p-4 md:p-5 font-bold text-gray-600 uppercase text-xs tracking-wider">Project</th>
                                <th className="p-4 md:p-5 font-bold text-gray-600 uppercase text-xs tracking-wider">Payment Status</th>
                                <th className="p-4 md:p-5 font-bold text-gray-600 text-center uppercase text-xs tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan={4} className="p-12 text-center"><Loader2 className="animate-spin text-red-600 mx-auto" size={32}/></td></tr>
                            ) : invoices.length === 0 ? (
                                <tr><td colSpan={4} className="p-12 text-center text-gray-400 font-medium text-lg">No invoices generated yet.</td></tr>
                            ) : (
                                invoices.map((inv) => (
                                    <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                                        {/* Client Details */}
                                        <td className="p-4 md:p-5 w-1/4">
                                            <div className="font-bold text-gray-900 text-sm whitespace-nowrap">{inv.clientName}</div>
                                            <div className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">{inv.clientEmail}</div>
                                            <div className="text-[11px] text-gray-400 mt-2 font-medium bg-gray-100 inline-block px-2 py-1 rounded">
                                                Gen: {new Date(inv.generatedAt).toLocaleDateString()}
                                            </div>
                                        </td>
                                        
                                        {/* Project */}
                                        <td className="p-4 md:p-5 w-1/4">
                                            <div className="text-sm font-semibold text-gray-700 max-w-[200px] truncate" title={inv.projectTitle}>
                                                {inv.projectTitle}
                                            </div>
                                        </td>
                                        
                                        {/* Payment Status Calculation */}
                                        <td className="p-4 md:p-5 w-1/4 whitespace-nowrap">
                                            <div className="space-y-1 w-40">
                                                <div className="text-xs flex justify-between">
                                                    <span className="text-gray-500 font-medium">Total: </span>
                                                    <span className="font-bold text-gray-700">₹{inv.totalAmount?.toLocaleString()}</span>
                                                </div>
                                                
                                                {inv.discountAmount > 0 && (
                                                    <div className="text-xs flex justify-between">
                                                        <span className="text-red-400 font-medium">Discount: </span>
                                                        <span className="font-bold text-red-500">-₹{inv.discountAmount?.toLocaleString()}</span>
                                                    </div>
                                                )}
                                                
                                                <div className="text-xs flex justify-between border-b pb-1">
                                                    <span className="text-green-600 font-medium">Received: </span>
                                                    <span className="font-bold text-green-700">₹{inv.advanceAmount?.toLocaleString()}</span>
                                                </div>

                                                <div className="pt-1 flex justify-between items-center">
                                                    <span className="text-xs font-bold text-gray-800 uppercase">Balance:</span>
                                                    {inv.remainingAmount <= 0 ? (
                                                        <span className="inline-flex items-center gap-1 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded font-black border border-green-200 tracking-wide">
                                                            <CheckCircle size={10}/> FULLY PAID
                                                        </span>
                                                    ) : (
                                                        <span className="font-black text-red-600 text-sm">₹{inv.remainingAmount?.toLocaleString()}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        
                                        {/* Actions */}
                                        <td className="p-4 md:p-5 w-1/4">
                                            <div className="flex items-center justify-center gap-2">
                                                {/* SEND EMAIL BUTTON */}
                                                <button 
                                                    onClick={() => handleSendEmail(inv.id)}
                                                    disabled={sendingId === inv.id}
                                                    title="Email Invoice to Client"
                                                    className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold shadow-sm transition-all
                                                        ${sendingId === inv.id ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-md active:scale-95'}`}
                                                >
                                                    {sendingId === inv.id ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                                                    {sendingId === inv.id ? 'Sending...' : 'Send'}
                                                </button>
                                                
                                                {/* DOWNLOAD BUTTON */}
                                                <button 
                                                    onClick={() => handleDownload(inv.id)}
                                                    title="Download PDF"
                                                    className="inline-flex items-center justify-center p-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 border border-gray-300 transition-all shadow-sm active:scale-95"
                                                >
                                                    <Download size={16}/>
                                                </button>

                                                {/* DELETE BUTTON */}
                                                <button 
                                                    onClick={() => handleDelete(inv.id)}
                                                    disabled={deletingId === inv.id}
                                                    title="Delete Invoice"
                                                    className={`inline-flex items-center justify-center p-2 bg-white text-red-500 rounded-lg border border-gray-300 transition-all shadow-sm active:scale-95
                                                        ${deletingId === inv.id ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-50 hover:text-red-700 hover:border-red-200'}`}
                                                >
                                                    {deletingId === inv.id ? <Loader2 size={16} className="animate-spin"/> : <Trash2 size={16}/>}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default InvoiceHistory;